import React, {useReducer} from 'react';
import {createPortal} from 'react-dom';

import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {AnnotationIndicatorHost} from './AnnotationIndicatorHost';
import AnnotationNoteIndicator from './AnnotationNoteIndicator';
import {
  resolveIndicatorScroller,
  resolveIndicatorSibling
} from '../engine/annotationIndicatorPlacement';
import type {AnnotationNoteLine, MeasuredNote} from './annotationNoteLines';
import {groupNotesByLine, measureNotes} from './annotationNoteLines';
import {filterNotedAnnotations} from '../store/annotationSelectors';
import type {Annotation} from '../types';
import type {IndicatorRegion} from '../store/IndicatorRegionStore';
import {useIndicatorRegionStore} from '../store/IndicatorRegionStore';
import useOnRegionsShift from './useOnRegionsShift';

interface RegionNote extends MeasuredNote {
  container: HTMLElement;
}

/** Lines that join the DOM at one place; a null sibling leaves them at the pane's end. */
interface IndicatorGroup {
  sibling: Element | null;
  lines: AnnotationNoteLine[];
}

interface IndicatorPane {
  scroller: HTMLElement;
  groups: IndicatorGroup[];
}

/** A pane's lines, each joining the DOM after the region its first note is in. */
const groupPaneLines = (scroller: HTMLElement, notes: RegionNote[]): IndicatorGroup[] => {
  const containerOf = new Map<Annotation, HTMLElement>(
    notes.map(({annotation, container}) => [annotation, container])
  );
  const siblingOf = new Map<HTMLElement, Element | null>();
  const linesBySibling = new Map<Element | null, AnnotationNoteLine[]>();
  groupNotesByLine(notes).forEach((line) => {
    const container = containerOf.get(line.annotations[0]);
    if (container && !siblingOf.has(container)) {
      siblingOf.set(container, resolveIndicatorSibling(container, scroller));
    }
    const sibling = (container && siblingOf.get(container)) ?? null;
    linesBySibling.set(sibling, [...(linesBySibling.get(sibling) ?? []), line]);
  });
  return [...linesBySibling].map(([sibling, lines]) => ({sibling, lines}));
};

/** Each pane's noted marks merged into lines, one entry per pane that flags any. */
function measureIndicatorPanes(
  regions: readonly IndicatorRegion[],
  annotationsByRegionId: Record<string, Annotation[]>
): IndicatorPane[] {
  const notesByScroller = new Map<HTMLElement, RegionNote[]>();
  regions.forEach(({regionId, container, getProjection, getLang}) => {
    const noted = filterNotedAnnotations(annotationsByRegionId[regionId] ?? []);
    const scroller = noted.length > 0 ? resolveIndicatorScroller(container) : null;
    const projection = scroller ? getProjection() : null;
    if (!scroller || !projection) {
      return;
    }
    const measured = measureNotes(noted, projection, scroller, getLang()).map((note) => ({
      ...note,
      container
    }));
    if (measured.length > 0) {
      notesByScroller.set(scroller, [...(notesByScroller.get(scroller) ?? []), ...measured]);
    }
  });
  return [...notesByScroller].map(([scroller, notes]) => ({
    scroller,
    groups: groupPaneLines(scroller, notes)
  }));
}

// Keyed by element, not index: an index key remounts a surviving indicator's open peek.
const elementKeys = new WeakMap<Element, string>();
let mintedElementKeys = 0;

const keyFor = (element: Element): string => {
  let key = elementKeys.get(element);
  if (!key) {
    mintedElementKeys += 1;
    key = `note-indicators-${mintedElementKeys}`;
    elementKeys.set(element, key);
  }
  return key;
};

/**
 * Every mounted region's noted marks, flagged by buttons in the margin of the
 * pane that scrolls each region. One layer reads all regions, so notes sharing a
 * line merge into one indicator even across regions — a table row's cells sit on
 * one visual line. The buttons join the DOM after the region they flag and
 * position absolutely against the pane.
 */
export default React.memo(function AnnotationNoteIndicators() {
  const regions = useIndicatorRegionStore((state) => state.regions);
  const annotationsByRegionId = useAnnotationModeStore((state) => state.annotationsByRegionId);
  const areAnnotationsHidden = useAnnotationModeStore((state) => state.areAnnotationsHidden);
  // The DOM moving is an input React can't see: a bump re-renders to re-measure,
  // and memo() keeps parent renders from paying for the measurement. Do not opt
  // this file into the React Compiler — it would memoize the measurement away.
  const [, onLayoutShift] = useReducer((count: number) => count + 1, 0);
  useOnRegionsShift(regions, onLayoutShift);

  if (areAnnotationsHidden) {
    return null;
  }

  const panes = measureIndicatorPanes(regions, annotationsByRegionId);

  return panes.flatMap(({scroller, groups}) =>
    groups.map(({sibling, lines}) => {
      const indicators = lines.map((line) => (
        <AnnotationNoteIndicator key={line.annotations[0].id} line={line} />
      ));
      return sibling ? (
        <AnnotationIndicatorHost
          key={keyFor(sibling)}
          sibling={sibling}
          side='after'
          pane={scroller}
        >
          {indicators}
        </AnnotationIndicatorHost>
      ) : (
        createPortal(indicators, scroller, keyFor(scroller))
      );
    })
  );
});
